import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../../../../../auth/AuthContext";
import { getFundDbName } from "../../../../../utils/funds";
// Serviço e tipos são os mesmos do entrego: os endpoints
// (/gestorBaixaListEntrego, /gestorBaixaDocStart, /gestorSubmitBaixaDb) e os
// shapes de resposta são idênticos — só o opsMae/ops no corpo muda. Duplicar
// aqui seria duplicação sem ganho nenhum.
import { baixaService } from "../../fund-baixa-resolution/services/baixa-service";
import {
  EntregoBaixaItem,
  EntregoOpsItem,
} from "../../fund-baixa-resolution/types";

export const imileBaixaKeys = {
  all: ["imile-baixa"] as const,
  ops: (dbName: string) => [...imileBaixaKeys.all, "ops", dbName] as const,
  list: (dbName: string, selectedOps: string[]) =>
    [...imileBaixaKeys.all, "list", dbName, selectedOps.join(",")] as const,
};

export function useImileBaixa() {
  const { user } = useAuth();
  const [selectedOps, setSelectedOps] = useState<string[]>([]);

  const xamaDbName = getFundDbName("imile");

  const {
    data: opsList = [],
    isPending: isPendingOps,
    refetch: refetchOps,
  } = useQuery({
    queryKey: imileBaixaKeys.ops(xamaDbName),
    queryFn: async () => {
      const response = await baixaService.getEntregoBaixaList({
        dbName: xamaDbName,
        opsMae: "imile",
        type: "ops",
      });
      return (response.data || []) as EntregoOpsItem[];
    },
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 120,
  });

  const {
    data: baixaList = [],
    isPending: isPendingBaixas,
    refetch: refetchBaixas,
  } = useQuery({
    queryKey: imileBaixaKeys.list(xamaDbName, selectedOps),
    queryFn: async () => {
      const response = await baixaService.getEntregoBaixaList({
        dbName: xamaDbName,
        opsMae: "imile",
        type: "baixa",
        listOps: selectedOps,
      });
      return (response.data || []) as EntregoBaixaItem[];
    },
    enabled: selectedOps.length > 0,
    staleTime: 1000 * 60 * 2,
  });

  const startDocMutation = useMutation({
    mutationFn: async ({
      filterDate,
      listOps,
    }: {
      filterDate: string;
      listOps: string[];
    }) => {
      if (!xamaDbName || !user?.email) throw new Error("Missing required data");
      return baixaService.startBaixaDoc({
        dbName: xamaDbName,
        filterDate,
        ops: "imile",
        listOps,
        userInfo: { email: user.email },
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetchBaixas();
      }, 3000);
    },
  });

  const submitDbMutation = useMutation({
    mutationFn: async ({
      filterDate,
      listOps,
    }: {
      filterDate: string;
      listOps: string[];
    }) => {
      if (!xamaDbName || !user?.email) throw new Error("Missing required data");
      return baixaService.submitBaixaDb({
        dbName: xamaDbName,
        filterDate,
        ops: "imile",
        listOps,
        userInfo: { email: user.email },
      });
    },
    onSuccess: () => {
      // Depois da baixa a lista de bases também muda (uma base pode ter ficado
      // sem crédito a baixar), então revalida as duas queries.
      setTimeout(() => {
        refetchBaixas();
        refetchOps();
      }, 3000);
    },
  });

  const startDoc = (filterDate: string, listOps: string[]) => {
    startDocMutation.mutate({ filterDate, listOps });
  };

  const submitDb = (filterDate: string, listOps: string[]) => {
    submitDbMutation.mutate({ filterDate, listOps });
  };

  return {
    xamaDbName,
    opsList,
    baixaList,
    selectedOps,
    setSelectedOps,
    isLoadingOps: isPendingOps,
    isLoadingBaixas: isPendingBaixas,
    startDoc,
    submitDb,
    isStartingDoc: startDocMutation.isPending,
    isSubmittingDb: submitDbMutation.isPending,
    refetchOps,
    refetchBaixas,
  };
}
